import { NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, inject, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { CUSTOM_FIELD_TYPES, GTM_EVENTS } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { CustomFieldModel, CustomFieldOptionModel } from '@models/custom-field.model';
import { TranslateService } from '@ngx-translate/core';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CustomFieldsService } from '@services/feature/custom-fields.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { HelperService } from '@services/utils/helper.service';
import { HostPageScrollService } from '@services/utils/host-page-scroll.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { BookingQuestionComponent } from '@ui-lib/booking-questions/booking-question/booking-question.component';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-booking-question-view',
  templateUrl: './booking-question-view.component.html',
  styleUrls: ['./booking-question-view.component.scss'],
  imports: [FormsModule, BookingQuestionComponent, NgTemplateOutlet, ButtonComponent, CalioTranslationPipe, CalioMeetingTemplatePipe]
})
export class BookingQuestionViewComponent implements OnInit {

  @Input() widgetQuestion: CustomFieldModel;
  @Input() isLast: boolean;
  @Input() isFirst: boolean;
  // TRUE when rendered inside the BEFORE-questions mount. The first question's back
  // button is only locked on the before-step (nothing precedes it) — the AFTER step's first
  // question must keep its back button even when isBeforeQuestionsMode() is also true (combined
  // before-questions + auto-load mode).
  @Input() isBeforeQuestionsStep = false;

  @Output() previousQuestionEvent = new EventEmitter<any>();
  @Output() nextQuestionEvent = new EventEmitter<any>();
  @Output() finishLastBookingQuestionEvent = new EventEmitter<any>();
  // Re-emitted pass-through from the child <app-booking-question>'s workerSelectAnswerInvalidated
  // — this view has no widgetQuestions array of its own to act on, so it just bubbles the signal
  // up to the parent (BookingQuestionsViewComponent) that owns the question list/activeQuestion.
  @Output() workerSelectAnswerInvalidatedEvent = new EventEmitter<number | undefined>();

  @ViewChild('bookingQuestionForm') public bookingQuestionForm: NgForm;

  protected showDependenciesGhostElement = false;
  protected dependenciesGhostElements = new Array<number>(2);
  protected isEndOfProcessByChildDependencies = false;
  protected lang: string;
  protected isNextButtonPressed = false;
  protected isGTMEventSent: boolean;
  protected templateConstant = TEMPLATE_CONSTANT;

  private hostPageScrollService = inject(HostPageScrollService);

  constructor(
    private customFieldsService: CustomFieldsService,
    private helperService: HelperService,
    public widgetService: WidgetService,
    public bookingService: BookingService,
    private translate: TranslateService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private swRouteService: SwRouteService,
    private elementRef: ElementRef
  ) {
    this.lang = this.translate.getFallbackLang();
    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.isGTMEventSent = false;
  }

  onRadioButtonChangeEvent(event: {
    customField: CustomFieldModel,
    customFieldOption: CustomFieldOptionModel,
    oldCustomFieldDependencyIds: number[],
    removeOldCustomFieldDependencyIds: boolean,
  }): void {
    this.isEndOfProcessByChildDependencies = false;
    if (event?.oldCustomFieldDependencyIds?.length > 0 && event.removeOldCustomFieldDependencyIds) {
      // Extends the ids through the stored entries' parent links, so a restored draft's
      // never-re-fetched questions are pruned too.
      this.bookingService.purgeStaleDependencyAnswers(
        event.oldCustomFieldDependencyIds,
        event.customField?.id,
        this.isBeforeQuestionsStep
      );
    }

    if (event?.customFieldOption) {
      let dependencies: string[] = [];

      if (
        Array.isArray(event?.customFieldOption) &&
        event?.customFieldOption?.length &&
        event.customFieldOption[0].dependencies &&
        event.customFieldOption[0].dependencies?.trim() !== ''
      ) {
        dependencies = event.customFieldOption[0].dependencies.split(',');
      } else if (event?.customFieldOption?.dependencies && event?.customFieldOption?.dependencies?.trim() !== '') {
        dependencies = event?.customFieldOption?.dependencies.split(',');
      }

      if (dependencies?.length > 0) {
        const cfDependencies = dependencies.map(i => Number(i));
        this.getCustomFieldDependencies(
          event.customField,
          cfDependencies
        );
      }
    }

    if (event.customField?.child_custom_field_id) {
      const customerSelectedValue = Array.isArray(event.customField.customerSelectedValue) && event.customField.customerSelectedValue?.length
        ? event.customField.customerSelectedValue[0]
        : event.customField.customerSelectedValue;
      if (customerSelectedValue) {
        this.getCustomFieldDependencies(
          event.customField,
          [event.customField.child_custom_field_id]
        );
      } else {
        delete event.customField.customFieldDependencies;
      }
    }
  }

  getCustomFieldDependencies(
    customFieldData: CustomFieldModel,
    cfDependencies: number[],
  ): void {
    this.isNextButtonPressed = false;
    this.showDependenciesGhostElement = true;
    this.customFieldsService
      .getCustomFieldsByIds(cfDependencies)
      .pipe(finalize(() => this.showDependenciesGhostElement = false))
      .subscribe({
        next: customFields => {
          if (customFields.length > 0) {
            // Resolve identifier-keyed prefill entries for these freshly-loaded dependency
            // children (both hidden and visible) before they render / are handled — each child's
            // BookingQuestionComponent reads getCustomFieldValues() by numeric id, so an
            // identifier URL key must be translated to the child's id first, else its prefill
            // silently no-ops. Idempotent + partial-list-safe, so re-running per child load is fine.
            this.swRouteService.resolveCustomFieldIdentifiers(customFields);
            for (const customField of customFields) {
              if (customField.is_end_of_process === 1) {
                this.isEndOfProcessByChildDependencies = true;
              }
              if ([
                CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT,
                CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO,
                CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD,
                CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD,
                CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD
              ].includes(customField.type)) {
                if (customField.alphabetically_sorted === 1) {
                  customField.custom_field_options = this.helperService.sortByNumber(customField.custom_field_options, 'label');
                } else {
                  customField.custom_field_options = this.helperService.sortByNumber(customField.custom_field_options, 'position');
                }
              }
            }

            for (const customField of customFields) {
              if (customField.is_hidden === 1) {
                this.bookingService.hiddenCustomFields.push(customField);
              }
            }

            customFields = customFields.filter(customField => customField.is_hidden === 0);

            customFieldData.customFieldDependencies = customFields;
            if (this.widgetService.isWebComponentMode) {
              const parentId = customFieldData.id;
              this.hostPageScrollService.requestReveal(() =>
                this.elementRef.nativeElement.querySelector('[data-cbsw-dependencies-of="' + parentId + '"]')
              );
            }
          } else {
            // Handle this case if dependencies are undefined
          }
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
  }

  customFieldChangeDetection(event: any): void {
    this.sendQuestionStartGtmEvent();

    // Proceeds to the next step if the one-page UI is enabled.
    setTimeout(() => {
      if (
        this.widgetService.widgetConf?.context?.enable_one_page_ui ||
        this.widgetService.widgetConf?.context?.auto_step_forward_on_select
      ) {
        !event?.dependencies && this.canAdvanceQuestion && this.finishLastBookingQuestion();
      }
    }, 200);
  }

  onWorkerSelectAnswerInvalidated(questionId?: number): void {
    this.workerSelectAnswerInvalidatedEvent.emit(questionId);
  }

  previousQuestion(): void {
    if (this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_questions_end);
    }
    this.isNextButtonPressed = false;
    this.isEndOfProcessByChildDependencies = false;
    this.previousQuestionEvent.emit();
  }

  /** A fully read-only question has all its controls disabled, so the NgForm reports status
   *  DISABLED (valid === false). There is nothing for the customer to fill in, so treat a
   *  disabled form as passable — otherwise a read-only question (e.g. a locked prefilled
   *  checkbox) permanently blocks the booking from advancing. */
  private get canAdvanceQuestion(): boolean {
    return !!(this.bookingQuestionForm?.valid || this.bookingQuestionForm?.disabled);
  }

  nextQuestion(): void {
    this.isNextButtonPressed = true;
    if (this.canAdvanceQuestion) {
      this.isEndOfProcessByChildDependencies = false;
      this.sendQuestionStartGtmEvent(true);
      this.nextQuestionEvent.emit();
    } else {
      this.bookingQuestionForm?.control?.markAllAsTouched();
      if (this.widgetService.isWebComponentMode) {
        this.hostPageScrollService.cancel();
      }
      this.helperService.scrollToFirstInvalidControl(this.elementRef.nativeElement);
    }
  }

  finishLastBookingQuestion(): void {
    this.isNextButtonPressed = true;
    if (this.canAdvanceQuestion) {
      this.sendQuestionStartGtmEvent(true);
      this.finishLastBookingQuestionEvent.emit();
    } else {
      this.bookingQuestionForm?.control?.markAllAsTouched();
      if (this.widgetService.isWebComponentMode) {
        this.hostPageScrollService.cancel();
      }
      this.helperService.scrollToFirstInvalidControl(this.elementRef.nativeElement);
    }
  }

  sendQuestionStartGtmEvent(endPreviousEvent = false): void {
    // trigger start event on first user interaction happen and
    // question step is configured to first step
    this.googleAnalyticsService.triggerApoointmentStartEvent();

    if (endPreviousEvent && !this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_questions_start);
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_questions_end);
      this.isGTMEventSent = true;
    } else if (endPreviousEvent && this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_questions_end);
    } else if (!endPreviousEvent && !this.isGTMEventSent) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_questions_start);
      this.isGTMEventSent = true;
    }
  }
}
